import React, { useState } from 'react';
import { OrganizationPicker } from '../common';
import { useParadigm } from '../../contexts/ParadigmContext';

const InviteUserModal = ({ organizations, onClose, onInvite }) => {
  const { labels, isSpeakerBureau } = useParadigm();
  const [formData, setFormData] = useState({
    email: '',
    role: 'ea',
    organization_id: '',
    notes: '',
    is_organization_admin: false  // Add organization admin flag
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      await onInvite(formData);
    } catch (error) {
      console.error('Failed to invite user:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-[70]">
      <div className="relative top-20 mx-auto p-5 border w-11/12 max-w-md shadow-lg rounded-md bg-white">
        <div className="mt-3">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-medium text-gray-900">Invite User to Organization</h3>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 text-xl"
            >
              ×
            </button>
          </div>
          
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Email Address <span className="text-red-500">*</span>
              </label>
              <input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                required
                placeholder="user@company.com"
              />
            </div>

            <div>
              <OrganizationPicker
                organizations={organizations}
                value={formData.organization_id}
                onChange={(orgId) => setFormData(prev => ({ ...prev, organization_id: orgId }))}
                placeholder="Select organization..."
                label="Organization"
                required
                showDomain
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Role <span className="text-red-500">*</span>
              </label>
              <select
                value={formData.role}
                onChange={(e) => setFormData(prev => ({ ...prev, role: e.target.value }))}
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
              >
                <option value="ea">{labels.ea}</option>
                {/* Speaker bureau orgs onboard speakers via portal invites from the
                    Speakers page, so the executive-login role isn't offered here */}
                {!isSpeakerBureau && <option value="executive">{labels.executive}</option>}
                <option value="other">Other</option>
              </select>
              {isSpeakerBureau && (
                <p className="mt-1 text-xs text-gray-500">
                  Speakers aren't invited here — use "Invite to portal" on their card on the Speakers page.
                </p>
              )}
            </div>

            {/* Organization Admin Designation */}
            <div>
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={formData.is_organization_admin}
                  onChange={(e) => setFormData(prev => ({ ...prev, is_organization_admin: e.target.checked }))}
                  className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                />
                <span className="ml-2 text-sm text-gray-700">
                  <span className="font-medium">Organization Admin</span>
                  <span className="text-gray-500 block text-xs">
                    Can invite and manage users within this organization
                  </span>
                </span>
              </label>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Notes (Optional)</label>
              <textarea
                value={formData.notes}
                onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                rows="3"
                placeholder="Additional notes for this invitation..."
              />
            </div>

            {/* Auto-approval notice */}
            <div className="bg-blue-50 border border-blue-200 rounded-md p-3">
              <div className="flex items-start">
                <svg className="w-4 h-4 text-blue-400 mt-0.5 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <div className="text-sm text-blue-700">
                  <strong>Admin invitations are automatically approved.</strong> The invited user will be able to access the organization immediately once they create their account.
                </div>
              </div>
            </div>
            
            <div className="flex justify-end space-x-3 pt-4">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-gray-600 bg-gray-100 rounded-md hover:bg-gray-200"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading || !formData.email.trim() || !formData.organization_id}
                className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-md disabled:opacity-50"
              >
                {loading ? 'Sending...' : 'Send Invitation'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default InviteUserModal;